/**
 * Command Palette & Global Search Modal (Requirement 10, 1.2).
 * Keyboard shortcut: Ctrl+K / Cmd+K.
 */

import { el } from './dom.js';

export interface CommandPaletteItem {
  id: string;
  category: 'Actions' | 'Data' | 'Format' | 'Navigation' | 'Help';
  icon: string;
  title: string;
  subtitle?: string;
  shortcut?: string;
  keywords?: string[];
  run: () => void;
}

export interface CommandPaletteOptions {
  items: CommandPaletteItem[];
  onClose?: () => void;
}

export interface CommandPalette {
  element: HTMLElement;
  open(initialQuery?: string): void;
  close(): void;
  isOpen(): boolean;
}

export function createCommandPalette(options: CommandPaletteOptions): CommandPalette {
  let isOpen = false;
  let activeIndex = 0;
  let filteredItems: CommandPaletteItem[] = [...options.items];

  const input = el('input', {
    className: 'rz-cmd-input',
    attrs: {
      type: 'text',
      placeholder: 'What do you want to do? (Type a command, tool or shortcut...)',
      autocomplete: 'off',
      spellcheck: 'false',
    },
  }) as HTMLInputElement;

  const list = el('div', { className: 'rz-cmd-list' });

  const backdrop = el('div', { className: 'rz-cmd-backdrop' }, [
    el('div', { className: 'rz-cmd-modal' }, [
      el('div', { className: 'rz-cmd-header' }, [
        el('span', { className: 'rz-cmd-icon', text: '⚡' }),
        input,
        el('span', { className: 'rz-cmd-badge', text: 'ESC to close' }),
      ]),
      list,
      el('div', { className: 'rz-cmd-footer' }, [
        el('div', { className: 'rz-cmd-hints' }, [
          el('span', { text: '↑↓ Navigate' }),
          el('span', { text: '↵ Execute' }),
          el('span', { text: 'ESC Close' }),
        ]),
        el('div', { className: 'rz-cmd-brand', text: 'NumixSheet Command Center' }),
      ]),
    ]),
  ]);

  backdrop.style.display = 'none';

  function renderList(): void {
    list.replaceChildren();

    if (filteredItems.length === 0) {
      list.appendChild(
        el('div', { className: 'rz-cmd-empty' }, [
          el('div', { className: 'rz-cmd-empty-icon', text: '🔍' }),
          el('div', { className: 'rz-cmd-empty-title', text: 'No matching commands' }),
          el('div', { className: 'rz-cmd-empty-sub', text: 'Try searching for "Import", "Sort", "Filter", "Chart", or "Format"' }),
        ]),
      );
      return;
    }

    let currentCategory = '';

    filteredItems.forEach((item, index) => {
      if (item.category !== currentCategory) {
        currentCategory = item.category;
        list.appendChild(el('div', { className: 'rz-cmd-category-header', text: currentCategory }));
      }

      const itemEl = el(
        'div',
        {
          className: `rz-cmd-item ${index === activeIndex ? 'is-active' : ''}`,
          attrs: { 'data-index': String(index) },
        },
        [
          el('span', { className: 'rz-cmd-item-icon', text: item.icon }),
          el('div', { className: 'rz-cmd-item-info' }, [
            el('div', { className: 'rz-cmd-item-title', text: item.title }),
            ...(item.subtitle ? [el('div', { className: 'rz-cmd-item-sub', text: item.subtitle })] : []),
          ]),
          ...(item.shortcut ? [el('kbd', { className: 'rz-cmd-item-kbd', text: item.shortcut })] : []),
        ],
      );

      itemEl.addEventListener('mouseenter', () => {
        activeIndex = index;
        updateActiveHighlight();
      });

      itemEl.addEventListener('click', (e) => {
        e.stopPropagation();
        close();
        item.run();
      });

      list.appendChild(itemEl);
    });

    scrollActiveIntoView();
  }

  function updateActiveHighlight(): void {
    const itemEls = list.querySelectorAll('.rz-cmd-item');
    itemEls.forEach((el, idx) => {
      el.classList.toggle('is-active', idx === activeIndex);
    });
  }

  function scrollActiveIntoView(): void {
    const activeEl = list.querySelector('.rz-cmd-item.is-active') as HTMLElement | null;
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }

  function filter(query: string): void {
    const q = query.toLowerCase().trim();
    if (!q) {
      filteredItems = [...options.items];
    } else {
      filteredItems = options.items.filter((item) => {
        return (
          item.title.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q) ||
          (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
          (item.keywords && item.keywords.some((k) => k.toLowerCase().includes(q))) ||
          (item.shortcut && item.shortcut.toLowerCase().includes(q))
        );
      });
    }
    activeIndex = 0;
    renderList();
  }

  input.addEventListener('input', () => {
    filter(input.value);
  });

  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredItems.length > 0) {
        activeIndex = (activeIndex + 1) % filteredItems.length;
        updateActiveHighlight();
        scrollActiveIntoView();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredItems.length > 0) {
        activeIndex = (activeIndex - 1 + filteredItems.length) % filteredItems.length;
        updateActiveHighlight();
        scrollActiveIntoView();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const targetItem = filteredItems[activeIndex];
      if (targetItem) {
        close();
        targetItem.run();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  });

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      close();
    }
  });

  function open(initialQuery = ''): void {
    isOpen = true;
    backdrop.style.display = 'flex';
    input.value = initialQuery;
    filter(initialQuery);
    setTimeout(() => input.focus(), 20);
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    backdrop.style.display = 'none';
    options.onClose?.();
  }

  return {
    element: backdrop,
    open,
    close,
    isOpen: () => isOpen,
  };
}
